import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NoResultGradientImage from '../../../../shared/components/images/no-result-gradient';
import SearchGrey from '../../../../shared/components/images/search-grey';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import {
  setAiSearchPopupInputValue,
  setShowAiSearchPopup,
  openAiChatHistory,
} from '../../v2-lead-slice';
import { getCurrentToken } from '../../../../shared/utils/token';
import {
  getAiSearchShortcut,
  userOsIdentifier,
  placeholderSuggestions,
  getUserRecentSearches,
} from './helper';
import { RootState } from '../../../../store/root-reducer';
import {
  contactAdmin,
  redirectWithoutRefresh,
  SubscriptionPlans,
} from '../../../../shared/utils';
import GlobalRestrictionModal from '../../../home/components/global-restriction-modal';
import { Routes } from '../../../../shared/routes';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';

/**
 *
 * @returns React Component
 */

const AiSearchBar: React.FC = () => {
  // ** REDUX Hooks
  const dispatch = useDispatch();
  const showAiSearchPopup = useSelector(
    (state: RootState) => state?.leadFinderV2?.showAiSearchPopup || false,
  );
  const aiSearchInputValue = useSelector(
    (state: RootState) => state?.leadFinderV2?.aiSearchInputValue,
  );
  const [isRestrictionModalShow, setIsRestrictionModalShow] = useState<boolean>(
    false,
  );
  const isFilterRestrictionEnabled = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isFilterRestrictionEnabled,
  );
  const leadFinderPlanCode = useSelector(
    (state: RootState) => state?.home?.leadFinderSubscription?.planCode,
  );

  const isFreePlanRestrictionEnabled = useMemo(() => (
      isFilterRestrictionEnabled === 1 &&
      leadFinderPlanCode === SubscriptionPlans.LEAD_FINDER_FREE
    ), [isFilterRestrictionEnabled, leadFinderPlanCode]);
  const userEmail = useSelector((state: RootState) => state?.home?.email);

  // ** Local State
  const [shortcut, setShortcut] = useState<string>('');
  const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);
  const [animationKey, setAnimationKey] = useState<number>(0);
  const [hasConversations, setHasConversations] = useState(false);

  // ** Memoized values
  const hasRecentSearch = useMemo(
    () => getUserRecentSearches(userEmail)?.length > 0,
    [userEmail],
  );

  // ** Handlers
  const openAiSearchPopup = useCallback(() => {
    if (isFreePlanRestrictionEnabled) {
      setIsRestrictionModalShow(true);
      return;
    }
    if (!showAiSearchPopup) {
      dispatch(setShowAiSearchPopup(true));
    }
  }, [dispatch, showAiSearchPopup, isFreePlanRestrictionEnabled]);

  const handleOpenChatHistory = useCallback(() => {
    if (isFreePlanRestrictionEnabled) {
      setIsRestrictionModalShow(true);
      return;
    }
    dispatch(openAiChatHistory());
  }, [dispatch, isFreePlanRestrictionEnabled]);

  const handleSuggestionClick = useCallback(
    (text: string) => {
      dispatch(setAiSearchPopupInputValue(text));
      dispatch(setShowAiSearchPopup(true));
    },
    [dispatch],
  );

  // ** Effects
  useEffect(() => {
    const platform = getAiSearchShortcut(userOsIdentifier());
    setShortcut(platform);
  }, []);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || '';
    const token = getCurrentToken();
    fetch(`${apiBase}/lead-finder/v2/leads/ai-chat/conversations`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        const list = data?.payload ?? data ?? [];
        setHasConversations(Array.isArray(list) && list.length > 0);
      })
      .catch(() => setHasConversations(false));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % placeholderSuggestions?.length;
        setAnimationKey((prevKey) => prevKey + 1);
        return newIndex;
      });
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="ai-search-bar-container">
      <div className="search-bar-suggestion-container d-flex">
        <div className="search-bar-header">
          <div className="search-bar-header-text">
            <NoResultGradientImage />
            <span>Accelerate Lead Discovery with AI Search</span>
            <span>Beta</span>
          </div>
          {(() => {
            const searchBarContent = (
              <div
                className={`search-bar-input-conatiner${
                  isFreePlanRestrictionEnabled ? ' disabled' : ''
                }`}
                {...accessibleOnClick(openAiSearchPopup)}
              >
                <span>
                  <SearchGrey />
                </span>
                <input
                  type="text"
                  className="search-bar-input"
                  onFocus={openAiSearchPopup}
                  value={aiSearchInputValue}
                  // disabled={isFreePlanRestrictionEnabled}
                  readOnly
                />
                <div className="search-bar-shortcut-key">
                  <NoResultGradientImage />
                  <div className="shortcut-key">{shortcut}</div>
                </div>
                {/* Placeholder Animation block */}
                {aiSearchInputValue?.length === 0 && (
                  <>
                    <div
                      key={`previous-${animationKey}`}
                      className="animation-placeholder previous"
                    >
                      {placeholderIndex > 0
                        ? placeholderSuggestions[placeholderIndex - 1]
                        : placeholderSuggestions[
                            placeholderSuggestions?.length - 1
                          ]}
                    </div>
                    <div
                      key={`current-${animationKey}`}
                      className="animation-placeholder current"
                    >
                      {placeholderSuggestions[placeholderIndex]}
                    </div>
                  </>
                )}
              </div>
            );
            return searchBarContent;
          })()}
        </div>

        <button
          type="button"
          className={`ai-search-bar-history-btn${hasConversations ? '' : ' ai-search-bar-history-btn--hidden'}`}
          onClick={hasConversations ? handleOpenChatHistory : undefined}
        >
          <i className="sh-time" />
          <span>View past conversations</span>
        </button>

        {/* <span
          className={`ai-suggestion-header ${hasRecentSearch ? 'hidden' : ''}`}
        >
          AI Suggestions
        </span>
        <div
          className={`search-ai-suggestion ${hasRecentSearch ? 'hidden' : ''}`}
        >
          {aiSuggestions?.map((item, index) => (
            <div
              className="ai-suggestion-first"
              key={`ai-suggestion ${index + 1}`}
              {...accessibleOnClick(() => handleSuggestionClick(item))}
            >
              <span>{item}</span>
            </div>
          ))}
        </div> */}
      </div>
      {isRestrictionModalShow && (
        <GlobalRestrictionModal
          show={isRestrictionModalShow}
          onClose={() => setIsRestrictionModalShow(false)}
          title="Upgrade your plan to use AI Search"
          content="Your current plan doesn’t allow you to use AI Search."
          primaryBtnLabel="Upgrade"
          onSubmit={() => {
            if (hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) {
              redirectWithoutRefresh(
                Routes.Setting_Billing_Upgrade_Lead_Finder_Plan,
              );
            } else {
              contactAdmin(
                'Upgrade request',
                'Please upgrade my account to Pro to unlock premium filters.',
              );
            }
          }}
        />
      )}
    </div>
  );
};

export default memo(AiSearchBar);
